import React from "react"
import { Box, Button, useTheme, Tooltip, alpha, Typography } from "@mui/material"
import { useRouter } from "next/router"
import { useSession } from "next-auth/react"
import { signOut } from "next-auth/react"
import { useTranslation } from "@/contexts/LanguageContext"
import { APP_MENU_ITEMS } from "@/constants/menuItems"
import { useUserPreferences } from "@/hooks/useUserPreferences"
import { isSessionAdmin } from "@/utils/auth"

export function HorizontalMenu() {
  const theme = useTheme()
  const router = useRouter()
  const { data: session } = useSession()
  const { t } = useTranslation()
  const { preferences } = useUserPreferences()
  const isAdmin = isSessionAdmin(session)

  const hiddenMenuItems = React.useMemo(() => new Set(preferences.hiddenMenuItems || []), [preferences.hiddenMenuItems])

  const menuItems = React.useMemo(() => {
    const roles = Array.isArray(session?.user?.roles) ? session.user.roles : []
    const rolesSet = new Set(roles)
    const hasCustomRoles = rolesSet.size > 0

    // Filter out admin-only items (they'll be shown in the user menu instead)
    // Also filter out user-settings (it's only accessible from the header user menu)
    const adminOnlyIds = new Set([
      "settings",
      "docs",
      "user-manual",
      "user-access",
      "automation",
      "user-settings",
      "job-monitoring",
      "job-toggles",
      "alfredo-settings",
      "alfredo-import-manager",
      "pipeline-diagnostics",
      "hot-deals-zones",
      "favorites",
      "disliked"
    ])

    return APP_MENU_ITEMS.filter((item) => {
      // Exclude admin-only items from horizontal menu
      if (adminOnlyIds.has(item.id)) {
        return false
      }
      // Exclude globally-hidden items and user-hidden items
      if (item.hidden || hiddenMenuItems.has(item.id)) {
        return false
      }
      if (item.action === "signout") {
        return false // Sign out is in user menu
      }
      if (isAdmin) {
        return true
      }
      if (rolesSet.has(item.id)) {
        return true
      }
      if (!hasCustomRoles && !item.adminOnly) {
        return true
      }
      return false
    })
  }, [isAdmin, session, hiddenMenuItems])

  const handleMenuItemClick = (item: (typeof APP_MENU_ITEMS)[0]) => {
    if (item.action === "signout") {
      signOut()
      return
    }

    if (item.href) {
      window.open(item.href, "_blank")
      return
    }

    if (item.route !== undefined) {
      if (item.route.startsWith("http")) {
        window.open(item.route, "_blank")
      } else if (item.route && item.route.length > 0) {
        router.push(`/features/${item.route}`)
      } else {
        // Empty route means dashboard - go to /features
        router.push("/features")
      }
    } else {
      // No route defined, default to dashboard
      router.push("/features")
    }
  }

  const isActive = (item: (typeof APP_MENU_ITEMS)[0]) => {
    if (item.route) {
      const currentPath = router.asPath
      if (item.route.length === 0) {
        return currentPath === "/features" || currentPath === "/" || currentPath === "/#"
      }
      return currentPath.startsWith(`/features/${item.route}`)
    }
    return false
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        justifyContent: "center",
        overflowX: "auto",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none"
      }}>
      {menuItems.map((item) => {
        const label = t(item.labelKey)
        const IconComponent = item.icon
        const active = isActive(item)

        return (
          <Tooltip key={item.id} title={label} arrow>
            <Button
              onClick={() => handleMenuItemClick(item)}
              sx={{
                minWidth: "auto",
                px: 1.5,
                py: 0.75,
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: active ? 600 : 400,
                flexDirection: "column",
                gap: 0.25,
                "&:hover": {
                  backgroundColor: active ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.05),
                  color: active ? theme.palette.primary.main : theme.palette.text.primary
                },
                transition: "all 0.2s ease"
              }}>
              {React.createElement(IconComponent, { sx: { fontSize: "1.25rem" } })}
              <Typography component="span" sx={{ fontSize: "0.7rem", lineHeight: 1.2, display: "block" }}>
                {label}
              </Typography>
            </Button>
          </Tooltip>
        )
      })}
    </Box>
  )
}
